// ─── exportUtils.js ───────────────────────────────────────────────────────────
// Shared CSV download and PDF print utilities used across all pages.
// Import as: import { downloadCSV, printToPDF } from "./exportUtils";

export function downloadCSV(data, filename) {
  if (!data || !data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [
    keys.join(","),
    ...data.map(row =>
      keys.map(k => {
        const v = row[k] == null ? "" : String(row[k]);
        return v.includes(",") || v.includes('"') || v.includes("\n")
          ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(",")
    ),
  ].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename;
  a.click();
}

export function printToPDF(title, contentHtml, companyName = "CrossRegGuard") {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Syne:wght@600;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Mono',monospace;background:#fff;color:#0A2342;padding:12mm}
  .header{display:flex;align-items:center;gap:10px;margin-bottom:8mm;padding-bottom:4mm;border-bottom:3px solid #00D4FF}
  .logo-hex{width:28px;height:28px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);flex-shrink:0}
  .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:18px;color:#0A2342}
  .doc-title{font-family:'Syne',sans-serif;font-size:20px;font-weight:700;margin-bottom:2mm}
  .doc-meta{font-size:10px;color:#4A5568;margin-bottom:6mm}
  table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:6mm}
  th{background:#0A2342;color:#fff;padding:6px 8px;text-align:left;font-size:9px;letter-spacing:0.05em}
  td{padding:5px 8px;border-bottom:1px solid #E2E8F0;vertical-align:top}
  tr:nth-child(even) td{background:#F7FAFC}
  .badge{display:inline-block;padding:2px 6px;border-radius:3px;font-size:9px;font-weight:600}
  .badge-high{background:#FED7D7;color:#C53030}
  .badge-medium{background:#FEFCBF;color:#744210}
  .badge-low{background:#C6F6D5;color:#276749}
  .badge-critical{background:#FAB8C4;color:#97266D}
  .section{margin-bottom:6mm}
  .section-title{font-family:'Syne',sans-serif;font-weight:700;font-size:13px;color:#0A2342;margin-bottom:3mm;padding-bottom:1mm;border-bottom:1px solid #E2E8F0}
  .footer{margin-top:8mm;padding-top:3mm;border-top:1px solid #E2E8F0;font-size:9px;color:#8A9BB5;display:flex;justify-content:space-between}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="header"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
<div class="doc-title">${title}</div>
<div class="doc-meta">Generated: ${new Date().toLocaleString()} · ${companyName}</div>
${contentHtml}
<div class="footer"><span>CrossRegGuard — Confidential</span><span>${title} · ${new Date().toLocaleDateString()}</span></div>
</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 800);
}

// ─── E-041 / Security: Input sanitization helper ──────────────────────────────
// Strips HTML tags, null bytes, and trims whitespace from all user-supplied text.
// Apply before saving free-text fields to Supabase.
export function sanitize(value) {
  if (value == null) return "";
  return String(value)
    .replace(/<[^>]*>/g, "")   // strip HTML tags
    .replace(/\0/g, "")        // strip null bytes
    .trim();
}

// ─── XML Escape Helper ────────────────────────────────────────────────────────

function escXml(str) {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// ─── XML Download ─────────────────────────────────────────────────────────────

export function downloadXML(xmlString, filename) {
  const blob = new Blob([xmlString], { type: "application/xml" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── FinCEN SAR XML (Form 111 / SARX) ────────────────────────────────────────

export function generateSARXML(form, company) {
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const batchId = `CRG-SAR-${ts}`;
  const filingInstitution = escXml(form.filing_institution || company?.name || "Unknown Institution");

  // Split subject name
  const nameParts = (form.subject_name || "").trim().split(" ");
  const firstName = escXml(nameParts.slice(0, -1).join(" ") || nameParts[0] || "");
  const lastName = escXml(nameParts.length > 1 ? nameParts[nameParts.length - 1] : "");

  // Map ID type
  const idTypeMap = { "SSN/TIN": "T", "Passport": "P", "Driver's License": "D", "National ID": "O", "Other": "O" };
  const idTypeCode = idTypeMap[form.subject_id_type] || "O";

  // Suspicious activity type codes
  const actTypes = (form.suspicious_activity_type || []);
  const structuring = actTypes.includes("Structuring / Smurfing") ? "<StructuringIndicator>Y</StructuringIndicator>" : "";
  const layering = actTypes.includes("Layering") ? "<MoneyLaunderingIndicator>Y</MoneyLaunderingIndicator>" : "";
  const cryptoAbuse = actTypes.includes("Crypto / virtual asset abuse") ? "<CyberEventIndicator>Y</CyberEventIndicator>" : "";
  const bribery = actTypes.includes("PEP / sanctions link") ? "<BriberyCorruptionIndicator>Y</BriberyCorruptionIndicator>" : "";
  const wireTransfer = actTypes.includes("Unusual wire transfers") ? "<WireTransferIndicator>Y</WireTransferIndicator>" : "";

  return `<?xml version="1.0" encoding="UTF-8"?>
<!-- FinCEN BSA E-Filing SAR Batch XML - Generated by CrossRegGuard -->
<!-- Upload this file at: https://bsaefiling.fincen.treas.gov -->
<!-- Form: FinCEN SAR (Form 111) -->
<EFilingBatchXML batchId="${batchId}"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <EFilingSubmissionXML>
    <FilingDateText>${new Date().toISOString().slice(0, 10)}</FilingDateText>
    <FilingName>CrossRegGuard SAR Export</FilingName>
    <EFilingActivityXML>
      <FormTypeCode>SARX</FormTypeCode>
      <ActivityAssociation>
        <InitialReport>Y</InitialReport>
        <CorrectsAmendsPriorReport>N</CorrectsAmendsPriorReport>
      </ActivityAssociation>
      <Activity seqNum="1">
        <!-- Part I: Reporting Financial Institution Information -->
        <FilingInstitutionInfo>
          <FinancialInstitution>
            <OrganizationName>${filingInstitution}</OrganizationName>
            <PrimaryFederalRegulatorCode>FINCEN</PrimaryFederalRegulatorCode>
            <TINTypeCode>EIN</TINTypeCode>
          </FinancialInstitution>
          <ContactInfo>
            <PersonName>
              <RawFullLegalName>${escXml(form.created_by || company?.name || "")}</RawFullLegalName>
            </PersonName>
          </ContactInfo>
        </FilingInstitutionInfo>
        <!-- Part II: Suspicious Activity Information -->
        <SuspiciousActivity>
          <DateInitiallyDetected>${escXml(form.detection_date || "")}</DateInitiallyDetected>
          <TotalAmount>${form.amount ? parseFloat(form.amount).toFixed(2) : "0.00"}</TotalAmount>
          <CurrencyCodeTypeCode>${escXml(form.currency || "USD")}</CurrencyCodeTypeCode>
          <SuspectIndicator>${form.has_suspect !== false ? "Y" : "N"}</SuspectIndicator>
          <SuspiciousActivityClassification>
            ${structuring}
            ${layering}
            ${cryptoAbuse}
            ${bribery}
            ${wireTransfer}
            <OtherSuspiciousActivityTypeText>${escXml(actTypes.join("; "))}</OtherSuspiciousActivityTypeText>
          </SuspiciousActivityClassification>
          <ActivityIPAddress></ActivityIPAddress>
        </SuspiciousActivity>
        <!-- Part III: Subject Information -->
        <Party seqNum="1">
          <ActivityPartyTypeCode>SUB</ActivityPartyTypeCode>
          <PartyName>
            <RawIndividualLastName>${escXml(lastName)}</RawIndividualLastName>
            <RawIndividualFirstName>${escXml(firstName)}</RawIndividualFirstName>
            <RawFullLegalName>${escXml(form.subject_name || "")}</RawFullLegalName>
          </PartyName>
          <Address>
            <RawAddress>${escXml(form.subject_address || "")}</RawAddress>
          </Address>
          <PhoneNumber></PhoneNumber>
          <BirthDate>${escXml(form.subject_dob || "")}</BirthDate>
          <PartyIdentification>
            <TINTypeCode>${idTypeCode}</TINTypeCode>
            <TIN>${escXml(form.subject_id_number || "")}</TIN>
            <OtherIssuerCountryText></OtherIssuerCountryText>
          </PartyIdentification>
        </Party>
        <!-- Part IV: Account Information -->
        <Account seqNum="1">
          <AccountNumber>${escXml(form.account_number || "")}</AccountNumber>
          <PartyAccountAssociation>
            <AccountRoleCode>SUBJ</AccountRoleCode>
          </PartyAccountAssociation>
        </Account>
        <!-- Part V: Narrative -->
        <NarrativeActivity>
          <Narrative>${escXml(form.narrative || "")}</Narrative>
        </NarrativeActivity>
      </Activity>
    </EFilingActivityXML>
  </EFilingSubmissionXML>
</EFilingBatchXML>`;
}

// ─── FinCEN CTR XML (Form 112 / CTRX) ────────────────────────────────────────

export function generateCTRXML(form, company) {
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const batchId = `CRG-CTR-${ts}`;
  const filingInstitution = escXml(form.filing_institution || company?.name || "Unknown Institution");
  const nameParts = (form.subject_name || "").trim().split(" ");
  const firstName = escXml(nameParts.slice(0, -1).join(" ") || nameParts[0] || "");
  const lastName = escXml(nameParts.length > 1 ? nameParts[nameParts.length - 1] : "");
  const cashInAmt = form.is_cash_in ? parseFloat(form.transaction_amount || 0).toFixed(2) : "0.00";
  const cashOutAmt = !form.is_cash_in ? parseFloat(form.transaction_amount || 0).toFixed(2) : "0.00";
  const idTypeMap = { "SSN/TIN": "T", "Passport": "P", "Driver's License": "D", "National ID": "O", "Other": "O" };
  const idTypeCode = idTypeMap[form.subject_id_type] || "O";

  return `<?xml version="1.0" encoding="UTF-8"?>
<!-- FinCEN BSA E-Filing CTR Batch XML - Generated by CrossRegGuard -->
<!-- Upload this file at: https://bsaefiling.fincen.treas.gov -->
<!-- Form: FinCEN CTR (Form 112) -->
<EFilingBatchXML batchId="${batchId}"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <EFilingSubmissionXML>
    <FilingDateText>${new Date().toISOString().slice(0, 10)}</FilingDateText>
    <FilingName>CrossRegGuard CTR Export</FilingName>
    <EFilingActivityXML>
      <FormTypeCode>CTRX</FormTypeCode>
      <ActivityAssociation>
        <InitialReport>Y</InitialReport>
        <CorrectsAmendsPriorReport>N</CorrectsAmendsPriorReport>
      </ActivityAssociation>
      <Activity seqNum="1">
        <!-- Part I: Person(s) Involved in Transaction(s) -->
        <Party seqNum="1">
          <ActivityPartyTypeCode>${form.is_business ? "TORG" : "TPER"}</ActivityPartyTypeCode>
          <PartyName>
            ${form.is_business
              ? `<RawEntityName>${escXml(form.subject_name || "")}</RawEntityName>`
              : `<RawIndividualLastName>${escXml(lastName)}</RawIndividualLastName>
            <RawIndividualFirstName>${escXml(firstName)}</RawIndividualFirstName>`
            }
            <RawFullLegalName>${escXml(form.subject_name || "")}</RawFullLegalName>
          </PartyName>
          <Address>
            <RawAddress>${escXml(form.subject_address || "")}</RawAddress>
          </Address>
          <BirthDate>${escXml(form.subject_dob || "")}</BirthDate>
          <PartyIdentification>
            <TINTypeCode>${idTypeCode}</TINTypeCode>
            <TIN>${escXml(form.subject_id_number || "")}</TIN>
          </PartyIdentification>
        </Party>
        <!-- Part II: Amount and Type of Transaction(s) -->
        <CurrencyTransaction>
          <TotalCashInReceiveAmount>${cashInAmt}</TotalCashInReceiveAmount>
          <TotalCashOutAmount>${cashOutAmt}</TotalCashOutAmount>
          <TransactionDateText>${escXml(form.transaction_date || "")}</TransactionDateText>
          <CurrencyCodeTypeCode>${escXml(form.currency || "USD")}</CurrencyCodeTypeCode>
        </CurrencyTransaction>
        <!-- Account -->
        <Account seqNum="1">
          <AccountNumber>${escXml(form.account_number || "")}</AccountNumber>
          <PartyAccountAssociation>
            <AccountRoleCode>TRAN</AccountRoleCode>
          </PartyAccountAssociation>
        </Account>
        <!-- Part III: Financial Institution Where Transaction(s) Took Place -->
        <FilingInstitutionInfo>
          <FinancialInstitution>
            <OrganizationName>${filingInstitution}</OrganizationName>
            <PrimaryFederalRegulatorCode>FINCEN</PrimaryFederalRegulatorCode>
          </FinancialInstitution>
        </FilingInstitutionInfo>
      </Activity>
    </EFilingActivityXML>
  </EFilingSubmissionXML>
</EFilingBatchXML>`;
}

// ─── goAML STR XML ────────────────────────────────────────────────────────────

export function generateGoAMLXML(form, company) {
  const ts = new Date().toISOString();
  const reportRef = `CRG-STR-${Date.now()}`;
  const nameParts = (form.subject_name || "").trim().split(" ");
  const firstName = escXml(nameParts.slice(0, -1).join(" ") || nameParts[0] || "Unknown");
  const lastName = escXml(nameParts.length > 1 ? nameParts[nameParts.length - 1] : "Unknown");

  return `<?xml version="1.0" encoding="UTF-8"?>
<!-- goAML STR XML - Generated by CrossRegGuard -->
<!-- For goAML jurisdictions: UAE (goAML), Nigeria (NFIU), South Africa (FIC), -->
<!-- Kenya (FRC), Pakistan (FMU), Egypt (EMLCU), and others -->
<!-- Upload via your jurisdiction's goAML portal -->
<Report xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <rentity_id>${escXml(company?.licenseNo || "ENTER_INSTITUTION_CODE")}</rentity_id>
  <rentity_branch>001</rentity_branch>
  <submission_code>E</submission_code>
  <report_code>STR</report_code>
  <submission_date>${ts}</submission_date>
  <currency_code_local>${escXml(form.currency || "USD")}</currency_code_local>
  <reporting_person>
    <first_name>${escXml((form.created_by || "").split("@")[0] || "Compliance")}</first_name>
    <last_name>Officer</last_name>
    <email>${escXml(form.created_by || "")}</email>
  </reporting_person>
  <location>
    <address_type>C</address_type>
    <address>${escXml(company?.name || "")}</address>
    <city>Head Office</city>
    <country_code>${escXml((form.jurisdiction || "US").slice(0, 2).toUpperCase())}</country_code>
  </location>
  <reason>${escXml(form.narrative || "Suspicious activity detected. See narrative for details.")}</reason>
  <action>Subject account under enhanced monitoring. SAR filed with regulatory authority. Further investigation ongoing.</action>
  <report_indicators>
    ${(form.suspicious_activity_type || ["Other"]).map(t =>
      `<indicator>${escXml(t)}</indicator>`
    ).join("\n    ")}
  </report_indicators>
  <transaction>
    <transactionnumber>${reportRef}</transactionnumber>
    <transaction_description>${escXml((form.suspicious_activity_type || []).join(", ") || "Suspicious transaction")}</transaction_description>
    <date_transaction>${escXml(form.transaction_dates?.slice(0, 10) || form.detection_date || new Date().toISOString().slice(0, 10))}</date_transaction>
    <teller>001</teller>
    <amount_local>${form.amount ? parseFloat(form.amount).toFixed(2) : "0.00"}</amount_local>
    <from_funds_code>C</from_funds_code>
    <from_country>${escXml((form.jurisdiction || "US").slice(0, 2).toUpperCase())}</from_country>
    <to_funds_code>C</to_funds_code>
    <to_country>${escXml((form.jurisdiction || "US").slice(0, 2).toUpperCase())}</to_country>
    <involved_parties>
      <involved_party>
        <party_role>CUST</party_role>
        <person>
          <first_name>${firstName}</first_name>
          <last_name>${lastName}</last_name>
          <birthdate>${escXml(form.subject_dob || "")}</birthdate>
          <id_number>${escXml(form.subject_id_number || "")}</id_number>
          <id_type>${escXml(form.subject_id_type || "Passport")}</id_type>
          <nationality1>${escXml((form.jurisdiction || "US").slice(0, 2).toUpperCase())}</nationality1>
          <address>${escXml(form.subject_address || "")}</address>
          <phones></phones>
        </person>
        <account>
          <account_number>${escXml(form.account_number || "")}</account_number>
          <currency_code>${escXml(form.currency || "USD")}</currency_code>
          <institution_name>${escXml(form.filing_institution || company?.name || "")}</institution_name>
        </account>
      </involved_party>
    </involved_parties>
  </transaction>
</Report>`;
}

// ─── FinCEN SAR Form PDF (Form 111) ──────────────────────────────────────────

export function printSARFormPDF(form, company) {
  const jKey = (form.jurisdiction || company?.jurisdiction || "").toUpperCase().slice(0, 2);
  const filingLink = {
    US: "https://bsaefiling.fincen.treas.gov/main.html",
    CA: "https://www.fintrac-canafe.gc.ca/reporting-declaration/Info/rpt-eng",
    UK: "https://www.nationalcrimeagency.gov.uk/what-we-do/crime-threats/money-laundering-and-terrorist-financing/ukfiu",
    AU: "https://www.austrac.gov.au/business/core-compliance/reporting",
    SG: "https://www.mas.gov.sg/regulation/anti-money-laundering",
    AE: "https://www.centralbank.ae/en/node/1671",
  }[jKey] || "https://www.fatf-gafi.org/en/topics/financial-intelligence.html";

  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>FinCEN SAR — ${form.subject_name || "Subject"}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:10px;color:#000;background:#fff;padding:8mm}
  .gov-header{text-align:center;border:2px solid #000;padding:6px;margin-bottom:4px}
  .gov-header h1{font-size:14px;font-weight:bold;margin-bottom:2px}
  .gov-header p{font-size:9px}
  .form-number{display:flex;justify-content:space-between;font-size:9px;margin-bottom:8px}
  .section{border:1px solid #000;margin-bottom:4px}
  .section-header{background:#000;color:#fff;font-weight:bold;font-size:10px;padding:3px 6px;text-transform:uppercase;letter-spacing:0.05em}
  .field-row{display:flex;border-top:1px solid #000}
  .field{flex:1;padding:3px 5px;border-right:1px solid #000}
  .field:last-child{border-right:none}
  .field-label{font-size:8px;color:#555;text-transform:uppercase;letter-spacing:0.04em;display:block;margin-bottom:1px}
  .field-value{font-size:10px;font-weight:500;min-height:14px}
  .checkbox-row{display:flex;flex-wrap:wrap;gap:4px;padding:4px 6px;border-top:1px solid #000}
  .checkbox{display:flex;align-items:center;gap:3px;font-size:9px}
  .box{width:10px;height:10px;border:1px solid #000;display:inline-flex;align-items:center;justify-content:center;font-size:8px;font-weight:bold;flex-shrink:0}
  .narrative-box{border-top:1px solid #000;padding:5px;min-height:80px;font-size:10px;white-space:pre-wrap;line-height:1.5}
  .footer{margin-top:8px;display:flex;justify-content:space-between;font-size:8px;color:#555;border-top:1px solid #ccc;padding-top:4px}
  .stamp-box{border:1px solid #000;padding:4px 8px;display:inline-block;font-size:9px}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>

<div class="gov-header">
  <h1>SUSPICIOUS ACTIVITY REPORT (SAR)</h1>
  <p>FinCEN Form 111 — Bank Secrecy Act Filing</p>
  <p>Generated by CrossRegGuard &nbsp;|&nbsp; File at: ${filingLink}</p>
</div>
<div class="form-number">
  <span>FinCEN Form 111 (Revised)</span>
  <span>Filing Date: ${new Date().toLocaleDateString()}</span>
  <span>Status: <strong>${(form.status || "draft").toUpperCase()}</strong></span>
  ${form.filed_ref ? `<span>Reference: ${form.filed_ref}</span>` : ""}
</div>

<!-- Part I: Reporting Financial Institution -->
<div class="section">
  <div class="section-header">Part I — Reporting Financial Institution Information</div>
  <div class="field-row">
    <div class="field" style="flex:2"><span class="field-label">1. Institution Name</span><div class="field-value">${form.filing_institution || company?.name || ""}</div></div>
    <div class="field"><span class="field-label">2. Jurisdiction</span><div class="field-value">${form.jurisdiction || company?.jurisdiction || ""}</div></div>
    <div class="field"><span class="field-label">3. Filing Date</span><div class="field-value">${new Date().toLocaleDateString()}</div></div>
  </div>
  <div class="field-row">
    <div class="field"><span class="field-label">4. Contact Name</span><div class="field-value">${form.created_by || ""}</div></div>
    <div class="field"><span class="field-label">5. SAR Status</span><div class="field-value">${(form.status || "").toUpperCase()}</div></div>
    <div class="field"><span class="field-label">6. Filed Reference</span><div class="field-value">${form.filed_ref || ""}</div></div>
  </div>
</div>

<!-- Part II: Suspicious Activity Information -->
<div class="section">
  <div class="section-header">Part II — Suspicious Activity Information</div>
  <div class="field-row">
    <div class="field"><span class="field-label">7. Date Activity Initially Detected</span><div class="field-value">${form.detection_date || ""}</div></div>
    <div class="field"><span class="field-label">8. Transaction Dates</span><div class="field-value">${form.transaction_dates || ""}</div></div>
    <div class="field"><span class="field-label">9. Total Amount</span><div class="field-value">${form.amount ? `${parseFloat(form.amount).toLocaleString()} ${form.currency}` : ""}</div></div>
    <div class="field"><span class="field-label">10. Suspect Identified?</span><div class="field-value">${form.has_suspect !== false ? "YES" : "NO"}</div></div>
  </div>
  <div class="checkbox-row">
    <strong style="font-size:9px;margin-right:4px">11. Type of Suspicious Activity:</strong>
    ${["Structuring / Smurfing","Layering","Cash-intensive business","Funnel account","Trade-based ML","Real estate ML","Crypto / virtual asset abuse","Shell company","PEP / sanctions link","Unusual wire transfers","Rapid movement of funds","Dormant account activity","Inconsistent business profile","Other"].map(t =>
      `<div class="checkbox"><div class="box">${(form.suspicious_activity_type||[]).includes(t)?"✓":""}</div>${t}</div>`
    ).join("")}
  </div>
</div>

<!-- Part III: Subject Information -->
<div class="section">
  <div class="section-header">Part III — Subject Information</div>
  <div class="field-row">
    <div class="field" style="flex:2"><span class="field-label">12. Full Legal Name</span><div class="field-value">${form.subject_name || ""}</div></div>
    <div class="field"><span class="field-label">13. Date of Birth</span><div class="field-value">${form.subject_dob || ""}</div></div>
  </div>
  <div class="field-row">
    <div class="field" style="flex:2"><span class="field-label">14. Address</span><div class="field-value">${form.subject_address || ""}</div></div>
    <div class="field"><span class="field-label">15. ID Type</span><div class="field-value">${form.subject_id_type || ""}</div></div>
    <div class="field"><span class="field-label">16. ID Number</span><div class="field-value">${form.subject_id_number || ""}</div></div>
  </div>
  <div class="field-row">
    <div class="field"><span class="field-label">17. Account Number</span><div class="field-value">${form.account_number || ""}</div></div>
  </div>
</div>

<!-- Part IV: Narrative -->
<div class="section">
  <div class="section-header">Part IV — Narrative (required — min. 1 paragraph)</div>
  <div class="narrative-box">${form.narrative || "[No narrative entered]"}</div>
</div>

<!-- Part V: Filing Guidance -->
<div class="section">
  <div class="section-header">Part V — Filing Instructions</div>
  <div class="field-row">
    <div class="field" style="flex:3"><span class="field-label">Filing Portal</span><div class="field-value">${filingLink}</div></div>
    <div class="field"><span class="field-label">BSA Deadline</span><div class="field-value">${form.detection_date ? (() => { const d = new Date(form.detection_date); d.setDate(d.getDate() + (form.has_suspect !== false ? 30 : 60)); return d.toLocaleDateString(); })() : "30 days from detection"}</div></div>
  </div>
</div>

<div class="footer">
  <span>CrossRegGuard — Confidential Regulatory Filing</span>
  <span>Generated: ${new Date().toLocaleString()}</span>
  <div class="stamp-box">${form.filed_ref ? `FILED — Ref: ${form.filed_ref}` : "DRAFT — NOT YET FILED"}</div>
</div>

</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 800);
}

// ─── FinCEN CTR Form PDF (Form 112) ──────────────────────────────────────────

export function printCTRFormPDF(form, company) {
  const txDate = form.transaction_date ? new Date(form.transaction_date) : null;
  const deadlineDate = txDate ? new Date(txDate.getTime() + 15 * 24 * 60 * 60 * 1000) : null;

  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>FinCEN CTR — ${form.subject_name || "Subject"}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:10px;color:#000;background:#fff;padding:8mm}
  .gov-header{text-align:center;border:2px solid #000;padding:6px;margin-bottom:4px}
  .gov-header h1{font-size:14px;font-weight:bold;margin-bottom:2px}
  .gov-header p{font-size:9px}
  .form-number{display:flex;justify-content:space-between;font-size:9px;margin-bottom:8px}
  .section{border:1px solid #000;margin-bottom:4px}
  .section-header{background:#000;color:#fff;font-weight:bold;font-size:10px;padding:3px 6px;text-transform:uppercase;letter-spacing:0.05em}
  .field-row{display:flex;border-top:1px solid #000}
  .field{flex:1;padding:3px 5px;border-right:1px solid #000}
  .field:last-child{border-right:none}
  .field-label{font-size:8px;color:#555;text-transform:uppercase;letter-spacing:0.04em;display:block;margin-bottom:1px}
  .field-value{font-size:10px;font-weight:500;min-height:14px}
  .checkbox-row{display:flex;gap:12px;padding:4px 6px;border-top:1px solid #000;align-items:center}
  .checkbox{display:flex;align-items:center;gap:3px;font-size:9px}
  .box{width:10px;height:10px;border:1px solid #000;display:inline-flex;align-items:center;justify-content:center;font-size:8px;font-weight:bold;flex-shrink:0}
  .footer{margin-top:8px;display:flex;justify-content:space-between;font-size:8px;color:#555;border-top:1px solid #ccc;padding-top:4px}
  .stamp-box{border:1px solid #000;padding:4px 8px;display:inline-block;font-size:9px}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>

<div class="gov-header">
  <h1>CURRENCY TRANSACTION REPORT (CTR)</h1>
  <p>FinCEN Form 112 — Bank Secrecy Act Filing</p>
  <p>Generated by CrossRegGuard &nbsp;|&nbsp; File at: https://bsaefiling.fincen.treas.gov</p>
</div>
<div class="form-number">
  <span>FinCEN Form 112 (Revised)</span>
  <span>Transaction Date: ${form.transaction_date || ""}</span>
  <span>Status: <strong>${(form.status || "draft").toUpperCase()}</strong></span>
  <span>15-Day Deadline: ${deadlineDate ? deadlineDate.toLocaleDateString() : ""}</span>
</div>

<!-- Part I: Person(s) Involved in Transaction -->
<div class="section">
  <div class="section-header">Part I — Person(s) Involved in Transaction(s)</div>
  <div class="checkbox-row">
    <span><strong>Person is:</strong></span>
    <div class="checkbox"><div class="box">${!form.is_business ? "✓" : ""}</div> Individual</div>
    <div class="checkbox"><div class="box">${form.is_business ? "✓" : ""}</div> Business/Organization</div>
    <div class="checkbox"><div class="box">${form.is_cash_in ? "✓" : ""}</div> Cash In (Deposit)</div>
    <div class="checkbox"><div class="box">${!form.is_cash_in ? "✓" : ""}</div> Cash Out (Withdrawal)</div>
  </div>
  <div class="field-row">
    <div class="field" style="flex:2"><span class="field-label">1. Full Legal Name</span><div class="field-value">${form.subject_name || ""}</div></div>
    <div class="field"><span class="field-label">2. Date of Birth</span><div class="field-value">${form.subject_dob || ""}</div></div>
  </div>
  <div class="field-row">
    <div class="field" style="flex:2"><span class="field-label">3. Address</span><div class="field-value">${form.subject_address || ""}</div></div>
    <div class="field"><span class="field-label">4. ID Type</span><div class="field-value">${form.subject_id_type || ""}</div></div>
    <div class="field"><span class="field-label">5. ID Number / TIN</span><div class="field-value">${form.subject_id_number || ""}</div></div>
  </div>
  <div class="field-row">
    <div class="field"><span class="field-label">6. Account Number</span><div class="field-value">${form.account_number || ""}</div></div>
  </div>
</div>

<!-- Part II: Amount and Type of Transaction -->
<div class="section">
  <div class="section-header">Part II — Amount and Type of Transaction(s)</div>
  <div class="field-row">
    <div class="field"><span class="field-label">7. Transaction Date</span><div class="field-value">${form.transaction_date || ""}</div></div>
    <div class="field"><span class="field-label">8. Transaction Amount</span><div class="field-value">${form.transaction_amount ? `${parseFloat(form.transaction_amount).toLocaleString()} ${form.currency}` : ""}</div></div>
    <div class="field"><span class="field-label">9. Cash In Amount</span><div class="field-value">${form.is_cash_in ? `${parseFloat(form.transaction_amount||0).toLocaleString()} ${form.currency}` : "—"}</div></div>
    <div class="field"><span class="field-label">10. Cash Out Amount</span><div class="field-value">${!form.is_cash_in ? `${parseFloat(form.transaction_amount||0).toLocaleString()} ${form.currency}` : "—"}</div></div>
  </div>
</div>

<!-- Part III: Financial Institution -->
<div class="section">
  <div class="section-header">Part III — Financial Institution Where Transaction Took Place</div>
  <div class="field-row">
    <div class="field" style="flex:2"><span class="field-label">11. Institution Name</span><div class="field-value">${form.filing_institution || company?.name || ""}</div></div>
    <div class="field"><span class="field-label">12. Jurisdiction</span><div class="field-value">${form.jurisdiction || ""}</div></div>
  </div>
  <div class="field-row">
    <div class="field"><span class="field-label">13. Filing Date</span><div class="field-value">${new Date().toLocaleDateString()}</div></div>
    <div class="field"><span class="field-label">14. Filed Reference</span><div class="field-value">${form.filed_ref || ""}</div></div>
    <div class="field"><span class="field-label">15. 15-Day Filing Deadline</span><div class="field-value">${deadlineDate ? deadlineDate.toLocaleDateString() : ""}</div></div>
  </div>
</div>

<div class="footer">
  <span>CrossRegGuard — Confidential Regulatory Filing</span>
  <span>Generated: ${new Date().toLocaleString()}</span>
  <div class="stamp-box">${form.filed_ref ? `FILED — Ref: ${form.filed_ref}` : "DRAFT — NOT YET FILED"}</div>
</div>

</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 800);
}